import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type EndpointRow,
  type IntegrationRow,
  type WebhookSubscriptionRow,
  useIntegrationCredentials,
  useIntegrationEndpoints,
  useIntegrationMutations,
  useIntegrations,
  useWebhookSubscriptions,
} from '../hooks/api';
import { ErrorBox, Loading } from '../components/Loading';
import { Status } from '../components/Status';
import { formatTimestamp } from '../api';

const ENVIRONMENTS: Array<'sandbox' | 'live'> = ['sandbox', 'live'];
const AUTH_METHODS: EndpointRow['auth_method'][] = ['apikey', 'oauth2', 'hmac', 'basic', 'mtls'];
const SIGNATURE_METHODS: NonNullable<WebhookSubscriptionRow['signature_method']>[] = [
  'hmac-sha256',
  'mtls',
  'bearer',
  'none',
];
const WEBHOOK_STATUS: WebhookSubscriptionRow['status'][] = ['paused', 'active', 'rotating', 'retired'];
const WEBHOOK_RECEIVER_BASE_URL = 'https://leitstand.eperformances.com/webhooks';

export default function IntegrationsPage() {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  if (!tenantCode) return <ErrorBox error="kein tenantCode" />;
  return <IntegrationsPanel tenantCode={tenantCode} />;
}

export function IntegrationsPanel({ tenantCode }: { tenantCode: string }) {
  const integrations = useIntegrations(tenantCode);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!selectedId && integrations.data?.data.length) {
      const preferred = integrations.data.data.find((i) => i.code === 'pulpo') ?? integrations.data.data[0];
      if (preferred) setSelectedId(preferred.id);
    }
  }, [integrations.data, selectedId]);

  const selected = integrations.data?.data.find((i) => i.id === selectedId);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-neutral-300 pb-1">
        <div>
          <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700">
            Integrationen: <span className="font-mono normal-case">{tenantCode}</span>
          </h1>
          <div className="text-[11px] text-neutral-600 font-mono">
            core.integrations / core.integration_endpoints / core.integration_credentials / core.webhook_subscriptions
          </div>
        </div>
        <Link to={`/tenants/${encodeURIComponent(tenantCode)}`} className="btn">
          Zurueck
        </Link>
      </div>

      {integrations.isLoading && <Loading />}
      {integrations.error && <ErrorBox error={integrations.error} />}

      {integrations.data && (
        <div className="grid grid-cols-[300px_1fr] gap-3">
          <div className="panel">
            <div className="panel-h">Integrationen</div>
            <div className="panel-b p-0">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Typ</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {integrations.data.data.map((row) => (
                    <tr
                      key={row.id}
                      className={row.id === selected?.id ? 'bg-slate-50' : ''}
                    >
                      <td>
                        <button
                          type="button"
                          className="mono underline"
                          onClick={() => setSelectedId(row.id)}
                        >
                          {row.code}
                        </button>
                      </td>
                      <td className="mono">{row.type}</td>
                      <td>
                        {row.active ? <Status kind="ok">active</Status> : <Status kind="muted">prepared</Status>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selected && <IntegrationDetail tenantCode={tenantCode} integration={selected} />}
        </div>
      )}
    </div>
  );
}

function IntegrationDetail({ tenantCode, integration }: { tenantCode: string; integration: IntegrationRow }) {
  const endpoints = useIntegrationEndpoints(tenantCode, integration.id);
  const webhooks = useWebhookSubscriptions(tenantCode, integration.id);
  const mutations = useIntegrationMutations(tenantCode);

  return (
    <div className="space-y-3">
      <div className="panel">
        <div className="panel-h flex items-center justify-between">
          <span>
            {integration.code} <span className="font-mono text-[11px] text-neutral-500">vendor={integration.vendor}</span>
          </span>
          <button
            type="button"
            className="btn"
            disabled={mutations.updateIntegration.isPending}
            onClick={() =>
              mutations.updateIntegration.mutate({
                integrationId: integration.id,
                patch: { active: !integration.active },
              })
            }
          >
            {integration.active ? 'Deaktivieren' : 'Aktivieren'}
          </button>
        </div>
        <div className="panel-b grid grid-cols-4 gap-3 text-[12px]">
          <Kpi label="Code" value={integration.code} />
          <Kpi label="Typ" value={integration.type} />
          <Kpi label="Vendor" value={integration.vendor} />
          <div>
            <div className="text-[11px] text-neutral-600 mb-0.5">Status</div>
            {integration.active ? <Status kind="ok">active</Status> : <Status kind="warn">prepared</Status>}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">Endpoints + Credentials</div>
        <div className="panel-b space-y-3">
          {endpoints.isLoading && <Loading />}
          {endpoints.error && <ErrorBox error={endpoints.error} />}
          {ENVIRONMENTS.map((env) => (
            <EndpointEditor
              key={`${integration.id}-${env}`}
              tenantCode={tenantCode}
              integration={integration}
              environment={env}
              endpoint={endpoints.data?.data.find((e) => e.environment === env)}
            />
          ))}
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">Webhooks</div>
        <div className="panel-b p-0">
          {webhooks.isLoading && (
            <div className="p-2">
              <Loading />
            </div>
          )}
          {webhooks.error && (
            <div className="p-2">
              <ErrorBox error={webhooks.error} />
            </div>
          )}
          {webhooks.data && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Umgebung</th>
                  <th>Event</th>
                  <th>URL</th>
                  <th>Signatur</th>
                  <th>Secret-Ref</th>
                  <th>Status</th>
                  <th className="num">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.data.data.map((row) => (
                  <WebhookRow
                    key={row.id}
                    tenantCode={tenantCode}
                    integrationId={integration.id}
                    row={row}
                  />
                ))}
                <WebhookCreateRow tenantCode={tenantCode} integration={integration} />
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EndpointEditor({
  tenantCode,
  integration,
  environment,
  endpoint,
}: {
  tenantCode: string;
  integration: IntegrationRow;
  environment: 'sandbox' | 'live';
  endpoint: EndpointRow | undefined;
}) {
  const mutations = useIntegrationMutations(tenantCode);
  const defaultHeader = integration.vendor === 'weclapp' ? 'AuthenticationToken' : 'X-Pulpo-Api-Key';
  const headerFromConfig =
    endpoint?.config && typeof endpoint.config['header_name'] === 'string'
      ? String(endpoint.config['header_name'])
      : defaultHeader;
  const [baseUrl, setBaseUrl] = useState(endpoint?.base_url ?? '');
  const [authMethod, setAuthMethod] = useState<EndpointRow['auth_method']>(endpoint?.auth_method ?? 'apikey');
  const [headerName, setHeaderName] = useState(headerFromConfig);
  const [active, setActive] = useState(endpoint?.active ?? false);

  useEffect(() => {
    setBaseUrl(endpoint?.base_url ?? '');
    setAuthMethod(endpoint?.auth_method ?? 'apikey');
    setHeaderName(headerFromConfig);
    setActive(endpoint?.active ?? false);
  }, [endpoint?.id, endpoint?.base_url, endpoint?.auth_method, endpoint?.active, headerFromConfig]);

  const canSave = baseUrl.trim().length > 0;
  const isSaving = mutations.createEndpoint.isPending || mutations.updateEndpoint.isPending;

  function save() {
    const payload = {
      base_url: baseUrl.trim(),
      auth_method: authMethod,
      config: headerName.trim() ? { header_name: headerName.trim() } : {},
      active,
    };
    if (endpoint) {
      mutations.updateEndpoint.mutate({
        integrationId: integration.id,
        endpointId: endpoint.id,
        patch: payload,
      });
    } else {
      mutations.createEndpoint.mutate({
        integrationId: integration.id,
        payload: { environment, ...payload },
      });
    }
  }

  return (
    <div className="border border-neutral-300">
      <div className="bg-neutral-100 border-b border-neutral-300 px-2 py-1 text-[12px] font-semibold uppercase flex items-center justify-between">
        <span>{environment}</span>
        {endpoint ? (
          endpoint.active ? <Status kind="ok">active</Status> : <Status kind="muted">inactive</Status>
        ) : (
          <Status kind="warn">fehlt</Status>
        )}
      </div>
      <div className="p-2 grid grid-cols-[1fr_140px_170px_80px_100px] gap-2 items-end">
        <Field label="Base URL">
          <input className="input w-full mono" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Field>
        <Field label="Auth">
          <select className="input w-full" value={authMethod} onChange={(e) => setAuthMethod(e.target.value as EndpointRow['auth_method'])}>
            {AUTH_METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>
        <Field label="Header">
          <input className="input w-full mono" value={headerName} onChange={(e) => setHeaderName(e.target.value)} />
        </Field>
        <label className="flex items-center gap-1 text-[12px] pb-1">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          aktiv
        </label>
        <button type="button" className="btn" disabled={!canSave || isSaving} onClick={save}>
          Speichern
        </button>
      </div>
      {endpoint && (
        <div className="border-t border-neutral-300 p-2">
          <CredentialRefEditor tenantCode={tenantCode} integration={integration} endpoint={endpoint} />
        </div>
      )}
    </div>
  );
}

function CredentialRefEditor({
  tenantCode,
  integration,
  endpoint,
}: {
  tenantCode: string;
  integration: IntegrationRow;
  endpoint: EndpointRow;
}) {
  const credentials = useIntegrationCredentials(tenantCode, integration.id, endpoint.id);
  const mutations = useIntegrationMutations(tenantCode);
  const defaultRef = `eperf/${tenantCode}/${integration.vendor}/${endpoint.environment}/api-token`;
  const active = useMemo(
    () => credentials.data?.data.find((c) => c.active) ?? credentials.data?.data[0],
    [credentials.data],
  );
  const [secretRef, setSecretRef] = useState(active?.secrets_manager_ref ?? defaultRef);
  const [version, setVersion] = useState(active?.secret_version ?? '');

  useEffect(() => {
    setSecretRef(active?.secrets_manager_ref ?? defaultRef);
    setVersion(active?.secret_version ?? '');
  }, [active?.id, active?.secrets_manager_ref, active?.secret_version, defaultRef]);

  return (
    <div className="grid grid-cols-[1fr_160px_120px_180px] gap-2 items-end">
      <Field label="API-Token Secret-Ref">
        <input className="input w-full mono" value={secretRef} onChange={(e) => setSecretRef(e.target.value)} />
      </Field>
      <Field label="Version">
        <input className="input w-full mono" value={version} onChange={(e) => setVersion(e.target.value)} />
      </Field>
      <button
        type="button"
        className="btn"
        disabled={!secretRef.trim() || mutations.recordCredentialRef.isPending}
        onClick={() =>
          mutations.recordCredentialRef.mutate({
            integrationId: integration.id,
            endpointId: endpoint.id,
            payload: {
              secrets_manager_ref: secretRef.trim(),
              secret_version: version.trim() || null,
              active: true,
              reason: 'leitstand_reference_update',
            },
          })
        }
      >
        Ref speichern
      </button>
      <div className="text-[11px] text-neutral-600">
        {active ? `aktiv seit ${formatTimestamp(active.created_at)}` : 'keine aktive Referenz'}
      </div>
    </div>
  );
}

function WebhookRow({
  tenantCode,
  integrationId,
  row,
}: {
  tenantCode: string;
  integrationId: string;
  row: WebhookSubscriptionRow;
}) {
  const mutations = useIntegrationMutations(tenantCode);
  const [environment, setEnvironment] = useState<WebhookSubscriptionRow['environment']>(row.environment);
  const [receiverUrl, setReceiverUrl] = useState(row.receiver_url);
  const [signatureMethod, setSignatureMethod] = useState<NonNullable<WebhookSubscriptionRow['signature_method']>>(
    row.signature_method ?? 'none',
  );
  const [secretRef, setSecretRef] = useState(row.signature_secret_ref ?? '');
  const [status, setStatus] = useState<WebhookSubscriptionRow['status']>(row.status);

  useEffect(() => {
    setEnvironment(row.environment);
    setReceiverUrl(row.receiver_url);
    setSignatureMethod(row.signature_method ?? 'none');
    setSecretRef(row.signature_secret_ref ?? '');
    setStatus(row.status);
  }, [row.id, row.environment, row.receiver_url, row.signature_method, row.signature_secret_ref, row.status]);

  return (
    <tr>
      <td>
        <select className="input w-full" value={environment} onChange={(e) => setEnvironment(e.target.value as WebhookSubscriptionRow['environment'])}>
          {ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>{env}</option>
          ))}
        </select>
      </td>
      <td className="mono">{row.event_type}</td>
      <td>
        <input className="input w-full mono" value={receiverUrl} onChange={(e) => setReceiverUrl(e.target.value)} />
      </td>
      <td>
        <select className="input w-full" value={signatureMethod} onChange={(e) => setSignatureMethod(e.target.value as NonNullable<WebhookSubscriptionRow['signature_method']>)}>
          {SIGNATURE_METHODS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </td>
      <td>
        <input className="input w-full mono" value={secretRef} onChange={(e) => setSecretRef(e.target.value)} />
      </td>
      <td>
        <select className="input w-full" value={status} onChange={(e) => setStatus(e.target.value as WebhookSubscriptionRow['status'])}>
          {WEBHOOK_STATUS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </td>
      <td className="num">
        <button
          type="button"
          className="btn"
          disabled={!receiverUrl.trim() || mutations.updateWebhook.isPending}
          onClick={() =>
            mutations.updateWebhook.mutate({
              integrationId,
              webhookId: row.id,
              patch: {
                environment,
                receiver_url: receiverUrl.trim(),
                signature_method: signatureMethod,
                signature_secret_ref: secretRef.trim() || null,
                status,
              },
            })
          }
        >
          Speichern
        </button>
      </td>
    </tr>
  );
}

function WebhookCreateRow({ tenantCode, integration }: { tenantCode: string; integration: IntegrationRow }) {
  const mutations = useIntegrationMutations(tenantCode);
  const [environment, setEnvironment] = useState<WebhookSubscriptionRow['environment']>('sandbox');
  const [eventType, setEventType] = useState('');
  const [receiverUrl, setReceiverUrl] = useState(defaultWebhookReceiverUrl(integration.vendor, tenantCode, 'sandbox'));
  const [signatureMethod, setSignatureMethod] = useState<NonNullable<WebhookSubscriptionRow['signature_method']>>(
    integration.vendor === 'pulpo' ? 'hmac-sha256' : 'none',
  );
  const [secretRef, setSecretRef] = useState(
    integration.vendor === 'pulpo' ? `eperf/${tenantCode}/pulpo/sandbox/webhook-hmac-secret` : '',
  );

  useEffect(() => {
    setReceiverUrl(defaultWebhookReceiverUrl(integration.vendor, tenantCode, environment));
    setSignatureMethod(integration.vendor === 'pulpo' ? 'hmac-sha256' : 'none');
    setSecretRef(integration.vendor === 'pulpo' ? `eperf/${tenantCode}/pulpo/${environment}/webhook-hmac-secret` : '');
    setEventType('');
  }, [integration.id, integration.vendor, tenantCode, environment]);

  return (
    <tr className="bg-neutral-50">
      <td>
        <select className="input w-full" value={environment} onChange={(e) => setEnvironment(e.target.value as WebhookSubscriptionRow['environment'])}>
          {ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>{env}</option>
          ))}
        </select>
      </td>
      <td>
        <input className="input w-full mono" value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder={`${integration.vendor}.event`} />
      </td>
      <td>
        <input className="input w-full mono" value={receiverUrl} onChange={(e) => setReceiverUrl(e.target.value)} />
      </td>
      <td>
        <select className="input w-full" value={signatureMethod} onChange={(e) => setSignatureMethod(e.target.value as NonNullable<WebhookSubscriptionRow['signature_method']>)}>
          {SIGNATURE_METHODS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </td>
      <td>
        <input className="input w-full mono" value={secretRef} onChange={(e) => setSecretRef(e.target.value)} />
      </td>
      <td>
        <Status kind="warn">paused</Status>
      </td>
      <td className="num">
        <button
          type="button"
          className="btn"
          disabled={!eventType.trim() || !receiverUrl.trim() || mutations.createWebhook.isPending}
          onClick={() =>
            mutations.createWebhook.mutate({
              integrationId: integration.id,
              payload: {
                environment,
                event_type: eventType.trim(),
                receiver_url: receiverUrl.trim(),
                signature_method: signatureMethod,
                signature_secret_ref: secretRef.trim() || null,
                status: 'paused',
              },
            })
          }
        >
          Hinzufuegen
        </button>
      </td>
    </tr>
  );
}

function defaultWebhookReceiverUrl(
  vendor: string,
  tenantCode: string,
  environment: WebhookSubscriptionRow['environment'],
): string {
  return `${WEBHOOK_RECEIVER_BASE_URL}/${vendor}/${tenantCode}/${environment}`;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-neutral-600 mb-0.5">{label}</div>
      <div className="mono text-[12px]">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-neutral-600 mb-0.5">{label}</span>
      {children}
    </label>
  );
}
